import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { contractsApi, usersApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  FileText,
  Send,
  Inbox,
  Check,
  X,
  Clock,
  Coins,
  Calendar,
  User,
  Search,
  Loader2,
  AlertCircle,
  CheckCircle,
  XCircle,
  Trash2,
} from 'lucide-react';

const Contracts = () => {
  const { t, i18n } = useTranslation();
  const isItalian = i18n.language === 'it';
  const { user, isCaptain, refreshUser } = useAuth();
  
  const [durations, setDurations] = useState([]);
  const [sentContracts, setSentContracts] = useState([]);
  const [receivedContracts, setReceivedContracts] = useState([]);
  const [freePlayers, setFreePlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOfferDialog, setShowOfferDialog] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  const [offerForm, setOfferForm] = useState({
    duration: '12_months',
    message: '',
  });

  const fetchData = useCallback(async () => {
    try {
      const [durationsRes, receivedRes] = await Promise.all([
        contractsApi.getDurations(),
        contractsApi.getReceived(),
      ]);
      
      setDurations(durationsRes.data);
      setReceivedContracts(receivedRes.data);
      
      if (isCaptain) {
        const [sentRes, playersRes] = await Promise.all([
          contractsApi.getSent(),
          usersApi.list({ role: 'player' }),
        ]);
        setSentContracts(sentRes.data);
        // Filter free players (no club)
        const free = playersRes.data.filter(p => !p.club_id && p.profile_complete);
        setFreePlayers(free);
      }
    } catch (error) {
      console.error('Error fetching contracts:', error);
    } finally {
      setLoading(false);
    }
  }, [isCaptain]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSendOffer = async () => {
    if (!selectedPlayer) return;
    
    setSubmitting(true);
    try {
      await contractsApi.create({
        player_user_id: selectedPlayer.user_id,
        duration: offerForm.duration,
        message: offerForm.message || null,
      });
      
      toast.success(isItalian ? 'Offerta inviata!' : 'Offer sent!');
      setShowOfferDialog(false);
      setSelectedPlayer(null);
      setOfferForm({ duration: '12_months', message: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || (isItalian ? 'Errore invio' : 'Error sending offer'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRespond = async (contractId, accept) => {
    try {
      await contractsApi.respond(contractId, accept);
      toast.success(accept 
        ? (isItalian ? 'Contratto accettato!' : 'Contract accepted!') 
        : (isItalian ? 'Contratto rifiutato' : 'Contract rejected'));
      fetchData();
      if (accept && refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || (isItalian ? 'Errore' : 'Error'));
    }
  };

  const handleCancel = async (contractId) => {
    try {
      await contractsApi.cancel(contractId);
      toast.success(isItalian ? 'Offerta annullata' : 'Offer cancelled');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || (isItalian ? 'Errore' : 'Error'));
    }
  };

  const filteredPlayers = freePlayers.filter(p => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      p.gamertag?.toLowerCase().includes(query) ||
      p.playing_position?.toLowerCase().includes(query)
    );
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return (
          <span className="px-2 py-1 text-xs rounded bg-[#FFEA00]/20 text-[#FFEA00] border border-[#FFEA00]/30 flex items-center gap-1">
            <Clock size={12} />
            {isItalian ? 'In Attesa' : 'Pending'}
          </span>
        );
      case 'accepted':
        return (
          <span className="px-2 py-1 text-xs rounded bg-[#00E676]/20 text-[#00E676] border border-[#00E676]/30 flex items-center gap-1">
            <CheckCircle size={12} />
            {isItalian ? 'Accettato' : 'Accepted'}
          </span>
        );
      case 'rejected':
        return (
          <span className="px-2 py-1 text-xs rounded bg-[#FF1744]/20 text-[#FF1744] border border-[#FF1744]/30 flex items-center gap-1">
            <XCircle size={12} />
            {isItalian ? 'Rifiutato' : 'Rejected'}
          </span>
        );
      case 'cancelled':
        return (
          <span className="px-2 py-1 text-xs rounded bg-slate-500/20 text-slate-400 border border-slate-500/30 flex items-center gap-1">
            <X size={12} />
            {isItalian ? 'Annullato' : 'Cancelled'}
          </span>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col md:flex-row md:items-center md:justify-between gap-4"
      >
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight text-white flex items-center gap-3">
            <FileText size={32} className="text-[#00F0FF]" />
            {isItalian ? 'Contratti' : 'Contracts'}
          </h1>
          <p className="text-slate-400 mt-1">
            {isCaptain 
              ? (isItalian ? 'Invia e gestisci offerte di contratto' : 'Send and manage contract offers')
              : (isItalian ? 'Visualizza le offerte di contratto ricevute' : 'View received contract offers')}
          </p>
        </div>
      </motion.div>

      <Tabs defaultValue={isCaptain ? "send" : "received"} className="space-y-6">
        <TabsList className="bg-[#0a0d15] border border-white/10 p-1">
          {isCaptain && (
            <TabsTrigger
              value="send"
              className="data-[state=active]:bg-[#00F0FF] data-[state=active]:text-white uppercase tracking-wider text-xs font-bold"
              data-testid="tab-send"
            >
              <Send size={14} className="mr-2" />
              {isItalian ? 'Invia Offerta' : 'Send Offer'}
            </TabsTrigger>
          )}
          {isCaptain && (
            <TabsTrigger
              value="sent"
              className="data-[state=active]:bg-[#00F0FF] data-[state=active]:text-white uppercase tracking-wider text-xs font-bold"
              data-testid="tab-sent"
            >
              {isItalian ? 'Inviate' : 'Sent'} ({sentContracts.length})
            </TabsTrigger>
          )}
          <TabsTrigger
            value="received"
            className="data-[state=active]:bg-[#00F0FF] data-[state=active]:text-white uppercase tracking-wider text-xs font-bold"
            data-testid="tab-received"
          >
            <Inbox size={14} className="mr-2" />
            {isItalian ? 'Ricevute' : 'Received'} ({receivedContracts.length})
          </TabsTrigger>
        </TabsList>

        {/* Send Offer Tab - Captain Only */}
        {isCaptain && (
          <TabsContent value="send">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-[#0a0d15] border border-white/10 "
            >
              <div className="p-4 border-b border-white/10">
                <div className="flex items-center gap-4">
                  <div className="relative flex-1">
                    <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                    <Input
                      placeholder={isItalian ? 'Cerca giocatori liberi...' : 'Search free players...'}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 bg-[#050508] border-white/10 text-white"
                      data-testid="search-players"
                    />
                  </div>
                </div>
              </div>

              {filteredPlayers.length > 0 ? (
                <div className="divide-y divide-white/10 max-h-[500px] overflow-y-auto">
                  {filteredPlayers.map((player) => (
                    <div
                      key={player.user_id}
                      className="p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
                      data-testid={`player-${player.user_id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-[#050508] border border-white/10 flex items-center justify-center">
                          {player.picture ? (
                            <img src={player.picture} alt="" className="w-full h-full rounded-full object-cover" />
                          ) : (
                            <User size={20} className="text-[#00F0FF]" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-white">
                            {player.gamertag ? `${player.gamertag}` : 'Giocatore'}
                          </p>
                          {player.playing_position && (
                            <p className="text-sm text-[#00E676]">{player.playing_position}</p>
                          )}
                        </div>
                      </div>
                      <Button
                        className="bg-gradient-to-r from-[#00E676] to-[#00C853] hover:from-[#00C853] hover:to-[#00E676] text-black font-bold uppercase tracking-wider px-6 shadow-lg shadow-[#00E676]/20 transition-all hover:scale-105 hover:shadow-[#00E676]/40"
                        onClick={() => {
                          setSelectedPlayer(player);
                          setShowOfferDialog(true);
                        }}
                        data-testid={`offer-btn-${player.user_id}`}
                      >
                        <Send size={16} className="mr-2" />
                        {isItalian ? 'Offri' : 'Offer'}
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center">
                  <AlertCircle size={32} className="mx-auto text-slate-600 mb-2" />
                  <p className="text-slate-500">
                    {searchQuery 
                      ? (isItalian ? 'Nessun giocatore trovato' : 'No players found')
                      : (isItalian ? 'Nessun giocatore libero disponibile' : 'No free players available')}
                  </p>
                </div>
              )}
            </motion.div>
          </TabsContent>
        )}

        {/* Sent Contracts Tab - Captain Only */}
        {isCaptain && (
          <TabsContent value="sent">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-[#0a0d15] border border-white/10 "
            >
              {sentContracts.length > 0 ? (
                <div className="divide-y divide-white/10">
                  {sentContracts.map((contract) => (
                    <div
                      key={contract.contract_id}
                      className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-4 hover:bg-white/5"
                      data-testid={`sent-contract-${contract.contract_id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-[#050508] border border-white/10 flex items-center justify-center">
                          <User size={20} className="text-[#00F0FF]" />
                        </div>
                        <div>
                          <p className="font-medium text-white">
                            {contract.player_gamertag ? `${contract.player_gamertag}` : 'Giocatore'}
                          </p>
                          <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                            <span className="flex items-center gap-1">
                              <Calendar size={12} />
                              {contract.duration_months} {isItalian ? 'mesi' : 'months'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {getStatusBadge(contract.status)}
                        {contract.status === 'pending' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-slate-500 hover:text-[#FF1744]"
                            onClick={() => handleCancel(contract.contract_id)}
                            data-testid={`cancel-${contract.contract_id}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center">
                  <Send size={32} className="mx-auto text-slate-600 mb-2" />
                  <p className="text-slate-500">
                    {isItalian ? 'Nessuna offerta inviata' : 'No offers sent yet'}
                  </p>
                </div>
              )}
            </motion.div>
          </TabsContent>
        )}

        {/* Received Contracts Tab */}
        <TabsContent value="received">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-[#0a0d15] border border-white/10 "
          >
            {receivedContracts.length > 0 ? (
              <div className="divide-y divide-white/10">
                {receivedContracts.map((contract) => (
                  <div
                    key={contract.contract_id}
                    className="p-6 hover:bg-white/5"
                    data-testid={`received-contract-${contract.contract_id}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <div 
                            className="w-10 h-10 rounded flex items-center justify-center"
                            style={{ backgroundColor: '#2979FF' }}
                          >
                            <FileText size={20} className="text-white" />
                          </div>
                          <div>
                            <p className="font-heading text-lg font-bold text-white uppercase">
                              {contract.club_name}
                            </p>
                            <p className="text-xs text-slate-500">
                              {isItalian ? 'Offerta di Contratto' : 'Contract Offer'}
                            </p>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
                          <div className="bg-[#050508] border border-white/10  p-3">
                            <p className="text-xs text-slate-500 uppercase mb-1">
                              {isItalian ? 'Durata' : 'Duration'}
                            </p>
                            <p className="font-mono text-lg font-bold text-white">
                              {contract.duration_months} {isItalian ? 'mesi' : 'mo'}
                            </p>
                          </div>
                          <div className="bg-[#050508] border border-white/10  p-3">
                            <p className="text-xs text-slate-500 uppercase mb-1">
                              {isItalian ? 'Scade' : 'Expires'}
                            </p>
                            <p className="font-mono text-sm text-white">
                              {new Date(contract.expires_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>

                        {contract.message && (
                          <div className="mt-4 p-3 bg-[#050508] border border-white/10 ">
                            <p className="text-xs text-slate-500 uppercase mb-1">
                              {isItalian ? 'Messaggio' : 'Message'}
                            </p>
                            <p className="text-sm text-slate-300">"{contract.message}"</p>
                          </div>
                        )}
                      </div>

                      <div className="flex flex-row md:flex-col gap-2">
                        <Button
                          className="flex-1 bg-[#00E676] hover:bg-[#00E676]/80 text-black font-bold"
                          onClick={() => handleRespond(contract.contract_id, true)}
                          data-testid={`accept-${contract.contract_id}`}
                        >
                          <Check size={18} className="mr-2" />
                          {isItalian ? 'Accetta' : 'Accept'}
                        </Button>
                        <Button
                          className="flex-1"
                          variant="destructive"
                          onClick={() => handleRespond(contract.contract_id, false)}
                          data-testid={`reject-${contract.contract_id}`}
                        >
                          <X size={18} className="mr-2" />
                          {isItalian ? 'Rifiuta' : 'Reject'}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center">
                <Inbox size={32} className="mx-auto text-slate-600 mb-2" />
                <p className="text-slate-500">
                  {isItalian ? 'Nessuna offerta di contratto' : 'No contract offers'}
                </p>
              </div>
            )}
          </motion.div>
        </TabsContent>
      </Tabs>

      {/* Contract Offer Dialog */}
      <Dialog open={showOfferDialog} onOpenChange={setShowOfferDialog}>
        <DialogContent className="bg-[#0a0d15] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase tracking-wider">
              {isItalian ? 'Offerta di Contratto' : 'Contract Offer'}
            </DialogTitle>
          </DialogHeader>

          {selectedPlayer && (
            <div className="space-y-6 mt-4">
              {/* Player Info */}
              <div className="flex items-center gap-4 p-4 bg-[#050508] border border-white/10 ">
                <div className="w-14 h-14 rounded-full bg-[#00F0FF]/20 border border-[#00F0FF]/30 flex items-center justify-center">
                  <User size={24} className="text-[#00F0FF]" />
                </div>
                <div>
                  <p className="font-medium text-white text-lg">
                    {selectedPlayer.gamertag ? `${selectedPlayer.gamertag}` : 'Giocatore'}
                  </p>
                  {selectedPlayer.playing_position && (
                    <p className="text-[#00E676]">{selectedPlayer.playing_position}</p>
                  )}
                </div>
              </div>

              {/* Duration */}
              <div className="space-y-2">
                <Label className="text-slate-300 uppercase tracking-wider text-xs font-bold">
                  {isItalian ? 'Durata Contratto' : 'Contract Duration'}
                </Label>
                <Select
                  value={offerForm.duration}
                  onValueChange={(value) => setOfferForm(prev => ({ ...prev, duration: value }))}
                >
                  <SelectTrigger className="bg-[#050508] border-white/10 text-white" data-testid="duration-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0d15] border-white/10">
                    {durations.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.label} ({d.months} {isItalian ? 'mesi' : 'months'})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Message */}
              <div className="space-y-2">
                <Label className="text-slate-300 uppercase tracking-wider text-xs font-bold">
                  {isItalian ? 'Messaggio (Opzionale)' : 'Message (Optional)'}
                </Label>
                <Textarea
                  value={offerForm.message}
                  onChange={(e) => setOfferForm(prev => ({ ...prev, message: e.target.value }))}
                  className="bg-[#050508] border-white/10 text-white min-h-[80px]"
                  placeholder={isItalian ? 'Scrivi un messaggio al giocatore...' : 'Write a message to the player...'}
                  data-testid="message-input"
                />
              </div>

              {/* Send Button */}
              <Button
                className="w-full btn-primary"
                onClick={handleSendOffer}
                disabled={submitting}
                data-testid="send-offer-btn"
              >
                {submitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Send size={18} className="mr-2" />
                    {isItalian ? 'Invia Offerta' : 'Send Offer'}
                  </>
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Contracts;
